import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAccount, useSignMessage } from "wagmi";
import { watchAccount } from "@wagmi/core";
import instance from "../api";
import { config } from "../components/Wallet/WalletProvider";
import { useDisconnect } from "wagmi";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { signMessageAsync } = useSignMessage();

  const [user, setUser] = useState();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const logoutTimeoutRef = useRef(false);

  const handleAuth = async () => {
    if (!address || isAuthenticating) return;

    setIsAuthenticating(true);
    setLoading(true);

    try {
      const userData = { address, chain: 1 };
      const { data } = await instance.post(`/auth/requestMessage`, userData, {
        headers: { "content-type": "application/json" },
      });

      if (!data?.message) throw new Error("No message received for signing.");

      const signature = await signMessageAsync({ message: data.message });

      const { data: userResponse } = await instance.post(`/auth/verify`, {
        message: data.message,
        signature,
      });

      if (!userResponse) throw new Error("Authentication failed.");

      setUser(userResponse);
    } catch (error) {
      console.error("Auth error:", error);
    } finally {
      setLoading(false);
      setIsAuthenticating(false);
    }
  };
  const { disconnectAsync } = useDisconnect();

  const logout = async () => {
    setLoading(true);
    try {
      localStorage.removeItem("wagmi.cache"); // Removes Wagmi cache
      localStorage.removeItem("role"); // Removes Wagmi cache
      localStorage.removeItem("wc@2:client"); // Removes WalletConnect cache
      sessionStorage.clear(); // Clears any session data
      await disconnectAsync(); // Disconnects the wallet

      await instance.post(`/auth/logout`);
      setUser({
        address: "0x0000000000000000000000000000000000000000",
        owned: {
          boredapeyachtclub: [],
          "mutant-ape-yacht-club": [],
        },
      });
      const loginPath = window.location.pathname;
      if (loginPath !== "/login") {
        navigate("/");
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    instance
      .get(`/auth/authenticate`)
      .then(({ data }) => {
        if (data?.user) {
          setUser(data.user);
        }
      })
      .catch((err) => console.error("Authentication check failed:", err))
      .finally(() => setLoading(false));
  }, []);

  watchAccount(config, {
    onChange(data) {
      if (data.isDisconnected) {
        if (logoutTimeoutRef.current) {
          clearTimeout(logoutTimeoutRef.current);
        }

        logoutTimeoutRef.current = setTimeout(() => {
          if (localStorage.getItem("role") !== "admin") {
            logout();
          }

          logoutTimeoutRef.current = null; // Reset ref after logout
        }, 500); // Adjust delay as needed
      }
      if (data.address && data.address !== address) {
        // logout();
      }
    },
  });

  const login = async (username, password) => {
    setLoading(true);
    try {
      const response = await instance.post("/auth/signin", {
        username,
        password,
      });

      if (response.status === 200 && response.data) {
        setUser(response.data);
        localStorage.setItem("role", "admin");
      } else {
        throw new Error("Invalid login response.");
      }
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const [isMobileLandscape, setIsMobileLandscape] = useState(false);

  const checkMobileLandscape = () => {
    const isMobile = window.matchMedia("(max-width: 1000px)").matches; // Common mobile max width
    const isLandscape = window.matchMedia("(orientation: landscape)").matches;
    setIsMobileLandscape(isMobile && isLandscape);
  };

  useEffect(() => {
    checkMobileLandscape(); // Check initially

    window.addEventListener("resize", checkMobileLandscape); // Check on window resize
    return () => {
      window.removeEventListener("resize", checkMobileLandscape); // Cleanup listener on component unmount
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isConnected,
        loading,
        handleAuth,
        login,
        logout,
        isMobileLandscape,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
